import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { FormBase } from '@shared/form-base.component';
import { Subject, Subscription } from 'rxjs';


@Component({
    selector: 'app-code-input',
    templateUrl: './code-input.component.html',
    styleUrls: ['./code-input.component.scss']
})
export class CodeInputComponent extends FormBase<any> implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @Input() formGroup: UntypedFormGroup;

    @Input() onReset: Subject<boolean>
    @Input() label = 'Para completar la verificación, ingresá el código de 6 dígitos.';


    @ViewChild('code1') code1Element: ElementRef;
    @ViewChild('code2') code2Element: ElementRef;
    @ViewChild('code3') code3Element: ElementRef;
    @ViewChild('code4') code4Element: ElementRef;
    @ViewChild('code5') code5Element: ElementRef;
    @ViewChild('code6') code6Element: ElementRef;


    private resetSubscription: Subscription = null;

    constructor() {
        super();
    }

    ngOnInit() {

        this._formInit();
    }

    ngAfterViewInit() {

        this.code1Element?.nativeElement.focus();
    }

    ngOnChanges(changes: SimpleChanges) {

        for (const propName in changes) {
            if (propName === 'formGroup') {
                const change = changes[propName];
                if (!change.isFirstChange() && (change.previousValue !== change.currentValue)) {

                    this._formInit();
                }
            }
        }
    }

    ngOnDestroy() {

        if (this.resetSubscription) this.resetSubscription.unsubscribe();
    }

    // Form

    onCodeKeyup(ev: KeyboardEvent, codeId: number) {

        if (this.formGroup) {
            if (this.formGroup.controls.codeConfirmation) {
                let codeConfirmation = this.code1.value + this.code2.value +
                    this.code3.value + this.code4.value +
                    this.code5.value + this.code6.value;

                this.formGroup.controls.codeConfirmation.setValue(codeConfirmation.toUpperCase().trim());
                this.formGroup.controls.codeConfirmation.setValidators([Validators.required, Validators.minLength(6)]);
                this.formGroup.controls.codeConfirmation.updateValueAndValidity;
            }

            if (ev.key.toLowerCase() == 'tab') return;

            if (ev.key.toLowerCase() == 'backspace') {
                // Delete the value
                if (codeId == 1) {
                    this.code1.setValue('');
                }
                if (codeId == 2) {
                    this.code2.setValue(''); this.code1Element.nativeElement.focus();
                }
                if (codeId == 3) {
                    this.code3.setValue(''); this.code2Element.nativeElement.focus();
                }
                if (codeId == 4) {
                    this.code4.setValue(''); this.code3Element.nativeElement.focus();
                }
                if (codeId == 5) {
                    this.code5.setValue(''); this.code4Element.nativeElement.focus();
                }
                if (codeId == 6) {
                    this.code6.setValue(''); this.code5Element.nativeElement.focus();
                }
                return;
            }

            // Set focus to next input
            if (codeId == 1 && this.code1.valid) this.code2Element.nativeElement.focus();
            if (codeId == 2 && this.code2.valid) this.code3Element.nativeElement.focus();
            if (codeId == 3 && this.code3.valid) this.code4Element.nativeElement.focus();
            if (codeId == 4 && this.code4.valid) this.code5Element.nativeElement.focus();
            if (codeId == 5 && this.code5.valid) this.code6Element.nativeElement.focus();
        }
    }

    onPaste(ev: any) {

        let pastedText = ev.clipboardData?.getData('text')?.trim();
        if (pastedText?.length >= 6) {

            pastedText = pastedText.slice(0, 6);

            this.code1.setValue(pastedText[0]);
            this.code2.setValue(pastedText[1]);
            this.code3.setValue(pastedText[2]);
            this.code4.setValue(pastedText[3]);
            this.code5.setValue(pastedText[4]);
            this.code6.setValue(pastedText[5]);

            this.code6Element.nativeElement.focus();

            if (this.formGroup.controls.codeConfirmation) {
                this.formGroup.controls.codeConfirmation.setValue(pastedText.toUpperCase().trim());
                this.formGroup.controls.codeConfirmation.setValidators([Validators.required, Validators.minLength(6)]);
                this.formGroup.controls.codeConfirmation.updateValueAndValidity;
            }
        }
    }


    get code1() { return this._formGet(this.formGroup, 'code1'); }
    get invalidCode1(): boolean {
        return this._isInvalid(this.formGroup, 'code1', this.touched);
    }

    get code2() { return this._formGet(this.formGroup, 'code2'); }
    get invalidCode2(): boolean {
        return this._isInvalid(this.formGroup, 'code2', this.touched);
    }

    get code3() { return this._formGet(this.formGroup, 'code3'); }
    get invalidCode3(): boolean {
        return this._isInvalid(this.formGroup, 'code3', this.touched);
    }

    get code4() { return this._formGet(this.formGroup, 'code4'); }
    get invalidCode4(): boolean {
        return this._isInvalid(this.formGroup, 'code4', this.touched);
    }

    get code5() { return this._formGet(this.formGroup, 'code5'); }
    get invalidCode5(): boolean {
        return this._isInvalid(this.formGroup, 'code5', this.touched);
    }

    get code6() { return this._formGet(this.formGroup, 'code6'); }
    get invalidCode6(): boolean {
        return this._isInvalid(this.formGroup, 'code6', this.touched);
    }

    get invalidCode(): boolean {
        return this.invalidCode1 || this.invalidCode2 || this.invalidCode3 ||
            this.invalidCode4 || this.invalidCode5 || this.invalidCode6;
    }


    private _formInit() {

        if (!this.formGroup) return;

        if (!this.formGroup.controls.code1) {
            this.formGroup.addControl('code1', new UntypedFormControl(
                '', [Validators.required, Validators.maxLength(1)]
            ));
        }
        if (!this.formGroup.controls.code2) {
            this.formGroup.addControl('code2', new UntypedFormControl(
                '', [Validators.required, Validators.maxLength(1)]
            ));
        }
        if (!this.formGroup.controls.code3) {
            this.formGroup.addControl('code3', new UntypedFormControl(
                '', [Validators.required, Validators.maxLength(1)]
            ));
        }
        if (!this.formGroup.controls.code4) {
            this.formGroup.addControl('code4', new UntypedFormControl(
                '', [Validators.required, Validators.maxLength(1)]
            ));
        }
        if (!this.formGroup.controls.code5) {
            this.formGroup.addControl('code5', new UntypedFormControl(
                '', [Validators.required, Validators.maxLength(1)]
            ));
        }
        if (!this.formGroup.controls.code6) {
            this.formGroup.addControl('code6', new UntypedFormControl(
                '', [Validators.required, Validators.maxLength(1)]
            ));
        }

        // Suscribir el evento del padre
        if (this.onReset) {
            this.resetSubscription = this.onReset.subscribe(x => this._cleanCodes());
        }
    }

    private _cleanCodes() {

        this.code1.setValue('');
        this.code2.setValue('');
        this.code3.setValue('');
        this.code4.setValue('');
        this.code5.setValue('');
        this.code6.setValue('');

        if (this.formGroup.controls.codeConfirmation) {
            this.formGroup.controls.codeConfirmation.setValue('');
            this.formGroup.controls.codeConfirmation.setValidators([Validators.required, Validators.minLength(6)]);
            this.formGroup.controls.codeConfirmation.updateValueAndValidity;
        }
    }

    // End Form
}
